import { ref } from "vue";

import { useCrypto } from "@shared/crypto/useCrypto";

import { useKeyStorage, type AuthMethod } from "./useKeyStorage";
import { useKeyWrap } from "./useKeyWrap";
import { useSession } from "./useSession";
import {
    touchIdGetSecret,
    touchIdRemoveSecret,
    touchIdStoreSecret,
} from "./useTouchId";

export type { AuthMethod };

const PRF_SALT = new TextEncoder().encode(
    "monster-chat-keys-v1"
) as Uint8Array<ArrayBuffer>;

const MAX_ATTEMPTS = 5;
const FAIL_KEY = "_sc_fc";
const UNTIL_KEY = "_sc_lu";

// Module-level so lockout state is shared across all useAuth() calls
const failCount = ref(parseInt(localStorage.getItem(FAIL_KEY) ?? "0", 10));
const lockoutUntil = ref(parseInt(localStorage.getItem(UNTIL_KEY) ?? "0", 10));

function checkLockout(): void {
    if (Date.now() < lockoutUntil.value) {
        const secs = Math.ceil((lockoutUntil.value - Date.now()) / 1000);
        throw new Error(`LOCKED:${secs}`);
    }
}

function recordFailure(): void {
    failCount.value++;
    localStorage.setItem(FAIL_KEY, String(failCount.value));
    if (failCount.value >= MAX_ATTEMPTS) {
        // 30s → 60s → 120s → … → max 1h
        const extra = failCount.value - MAX_ATTEMPTS;
        const delaySec = Math.min(30 * Math.pow(2, extra), 3600);
        lockoutUntil.value = Date.now() + delaySec * 1000;
        localStorage.setItem(UNTIL_KEY, String(lockoutUntil.value));
    }
}

function resetLockout(): void {
    failCount.value = 0;
    lockoutUntil.value = 0;
    localStorage.removeItem(FAIL_KEY);
    localStorage.removeItem(UNTIL_KEY);
}

export function useAuth() {
    const {
        generateKeyPairs,
        setKeys,
        encryptionKeyPair,
        signKeyPair,
        storageKey,
        generateStorageKey,
        setStorageKey,
    } = useCrypto();
    const storage = useKeyStorage();
    const { randomBytes, importAesGcm, deriveFromPin } = useKeyWrap();
    const session = useSession();

    // Ключ устройства (#5): у существующих пользователей его ещё нет —
    // при входе генерим и сохраняем под текущим методом (миграция).
    async function loadOrMigrateStorageKey(
        wrappingKey: CryptoKey,
        method: AuthMethod
    ): Promise<CryptoKey> {
        let sk = await storage.loadStorageKey(wrappingKey, method);
        if (!sk) {
            sk = await generateStorageKey();
            await storage.storeEncryptedStorageKey(sk, wrappingKey, method);
        }
        setStorageKey(sk);
        return sk;
    }

    async function registerPrf(name: string): Promise<void> {
        const { ecdh, sign } = await generateKeyPairs();
        await linkWithPrf(name, ecdh, sign);
    }

    // Регистрирует WebAuthn-credential с PRF и возвращает wrappingKey из его
    // PRF-output + rawId. Общее для первичной настройки и добавления биометрии.
    async function createPrfCredential(
        name: string
    ): Promise<{ wrappingKey: CryptoKey; rawId: ArrayBuffer }> {
        const credential = (await navigator.credentials.create({
            publicKey: {
                challenge: randomBytes(32),
                rp: { name: "Monster Chat" },
                user: { id: randomBytes(16), name, displayName: name },
                pubKeyCredParams: [
                    { type: "public-key", alg: -7 },
                    { type: "public-key", alg: -257 },
                ],
                authenticatorSelection: {
                    userVerification: "required",
                    authenticatorAttachment: "platform",
                },
                extensions: { prf: { eval: { first: PRF_SALT } } },
            },
        })) as PublicKeyCredential;

        const prfResult = credential.getClientExtensionResults().prf;
        if (!prfResult?.enabled && !prfResult?.results?.first) {
            throw new Error("PRF not supported by this authenticator");
        }

        let prfOutput: BufferSource;
        if (prfResult.results?.first) {
            prfOutput = prfResult.results.first;
        } else {
            const assertion = (await navigator.credentials.get({
                publicKey: {
                    challenge: randomBytes(32),
                    allowCredentials: [
                        { type: "public-key", id: credential.rawId },
                    ],
                    userVerification: "required",
                    extensions: { prf: { eval: { first: PRF_SALT } } },
                },
            })) as PublicKeyCredential;

            const output =
                assertion.getClientExtensionResults().prf?.results?.first;
            if (!output) {
                throw new Error("PRF output not returned");
            }
            prfOutput = output;
        }

        return {
            wrappingKey: await importAesGcm(prfOutput),
            rawId: credential.rawId,
        };
    }

    // Настройка биометрии (WebAuthn PRF) поверх ГОТОВЫХ identity-ключей — для
    // новой регистрации (только что сгенерированы) и для привязанного устройства
    // (импортированы из бандла). Регистрирует СВОЙ credential на этом устройстве
    // и оборачивает ключи + свой storageKey под его PRF-output.
    async function linkWithPrf(
        name: string,
        ecdh: CryptoKeyPair,
        sign: CryptoKeyPair
    ): Promise<void> {
        const { wrappingKey, rawId } = await createPrfCredential(name);
        await storage.storeEncryptedKeys(ecdh, sign, wrappingKey, "prf");
        await storage.storePublicKeys(ecdh, sign);
        await storage.storeCredentialId(new Uint8Array(rawId));
        await storage.setAuthMethods(["prf"]);
        setKeys(ecdh, sign);
        const sk = await generateStorageKey();
        await storage.storeEncryptedStorageKey(sk, wrappingKey, "prf");
        setStorageKey(sk);
        await session.save(ecdh, sign, sk);
    }

    async function registerPassword(password: string): Promise<void> {
        const { ecdh, sign } = await generateKeyPairs();
        await linkWithPassword(password, ecdh, sign);
    }

    async function registerTouchId(): Promise<void> {
        const { ecdh, sign } = await generateKeyPairs();
        await linkWithTouchId(ecdh, sign);
    }

    // Настройка Touch ID поверх ГОТОВЫХ identity-ключей — macOS-аналог
    // linkWithPrf (в Tauri/WKWebView WebAuthn PRF не работает, поэтому на маке
    // Touch ID ЗАМЕНЯЕТ PRF, а не дополняет). Секрет-обёртка — в Keychain за
    // биометрией.
    async function linkWithTouchId(
        ecdh: CryptoKeyPair,
        sign: CryptoKeyPair
    ): Promise<void> {
        const secret = randomBytes(32);
        await touchIdStoreSecret(secret);
        const wrappingKey = await importAesGcm(secret);
        await storage.storeEncryptedKeys(ecdh, sign, wrappingKey, "touchid");
        await storage.storePublicKeys(ecdh, sign);
        await storage.setAuthMethods(["touchid"]);
        setKeys(ecdh, sign);
        const sk = await generateStorageKey();
        await storage.storeEncryptedStorageKey(sk, wrappingKey, "touchid");
        setStorageKey(sk);
        await session.save(ecdh, sign, sk);
    }

    // Настройка PIN-входа поверх ГОТОВЫХ identity-ключей — для новой регистрации
    // (ключи только что сгенерированы) и для привязанного устройства (ключи
    // импортированы из бандла). storageKey у каждого устройства свой (per-device).
    async function linkWithPassword(
        password: string,
        ecdh: CryptoKeyPair,
        sign: CryptoKeyPair
    ): Promise<void> {
        const salt = randomBytes(16);
        const wrappingKey = await deriveFromPin(password, salt);
        await storage.storeEncryptedKeys(ecdh, sign, wrappingKey, "pin");
        await storage.storePublicKeys(ecdh, sign);
        await storage.storePinSalt(salt);
        await storage.setAuthMethods(["pin"]);
        setKeys(ecdh, sign);
        const sk = await generateStorageKey();
        await storage.storeEncryptedStorageKey(sk, wrappingKey, "pin");
        setStorageKey(sk);
        await session.save(ecdh, sign, sk);
    }

    // Добавить биометрию к уже настроенному аккаунту (ключи загружены):
    // оборачиваем ТЕКУЩИЕ ключи и storageKey под PRF нового credential'а,
    // ничего не пересоздавая.
    async function addPrfFallback(name: string): Promise<void> {
        if (!encryptionKeyPair.value || !signKeyPair.value) {
            throw new Error("Keys not loaded");
        }
        const { wrappingKey, rawId } = await createPrfCredential(name);
        await storage.storeEncryptedKeys(
            encryptionKeyPair.value,
            signKeyPair.value,
            wrappingKey,
            "prf"
        );
        if (storageKey.value) {
            await storage.storeEncryptedStorageKey(
                storageKey.value,
                wrappingKey,
                "prf"
            );
        }
        await storage.storeCredentialId(new Uint8Array(rawId));
        const methods = await storage.getAuthMethods();
        if (!methods.includes("prf")) {
            await storage.setAuthMethods([...methods, "prf"]);
        }
    }

    // Добавить Touch ID (Tauri macOS) к уже настроенному аккаунту: случайный
    // секрет-обёртка уходит в Keychain за биометрией, текущие ключи и
    // storageKey оборачиваются им под методом "touchid". Промпт Touch ID
    // покажет первое чтение секрета (при входе), не запись.
    async function addTouchIdFallback(): Promise<void> {
        if (!encryptionKeyPair.value || !signKeyPair.value) {
            throw new Error("Keys not loaded");
        }
        const secret = randomBytes(32);
        await touchIdStoreSecret(secret);
        const wrappingKey = await importAesGcm(secret);
        await storage.storeEncryptedKeys(
            encryptionKeyPair.value,
            signKeyPair.value,
            wrappingKey,
            "touchid"
        );
        if (storageKey.value) {
            await storage.storeEncryptedStorageKey(
                storageKey.value,
                wrappingKey,
                "touchid"
            );
        }
        const methods = await storage.getAuthMethods();
        if (!methods.includes("touchid")) {
            await storage.setAuthMethods([...methods, "touchid"]);
        }
    }

    // Вход по Touch ID: чтение секрета из Keychain показывает системный промпт.
    async function authenticateWithTouchId(): Promise<void> {
        const secret = await touchIdGetSecret();
        const wrappingKey = await importAesGcm(secret);
        const { ecdh, sign } = await storage.loadKeys(wrappingKey, "touchid");
        setKeys(ecdh, sign);
        const sk = await loadOrMigrateStorageKey(wrappingKey, "touchid");
        await session.save(ecdh, sign, sk);
    }

    // Проверка текущего PIN без загрузки ключей в стейт. Со счётчиком неудач и
    // локаутом — иначе смена PIN была бы оракулом для перебора.
    async function verifyPin(password: string): Promise<void> {
        checkLockout();
        const salt = await storage.getPinSalt();
        if (!salt) {
            throw new Error("No password salt stored");
        }
        const wrappingKey = await deriveFromPin(password, salt);
        try {
            await storage.loadKeys(wrappingKey, "pin");
            resetLockout();
        } catch (e) {
            if (e instanceof Error && e.message.startsWith("LOCKED:")) {
                throw e;
            }
            recordFailure();
            throw new Error("WRONG_PIN");
        }
    }

    // Смена PIN: проверяем старый, затем перевязываем текущие ключи и
    // storageKey на новый PIN с новой солью.
    async function changePin(oldPin: string, newPin: string): Promise<void> {
        if (!encryptionKeyPair.value || !signKeyPair.value) {
            throw new Error("Keys not loaded");
        }
        await verifyPin(oldPin);
        const salt = randomBytes(16);
        const wrappingKey = await deriveFromPin(newPin, salt);
        await storage.storeEncryptedKeys(
            encryptionKeyPair.value,
            signKeyPair.value,
            wrappingKey,
            "pin"
        );
        if (storageKey.value) {
            await storage.storeEncryptedStorageKey(
                storageKey.value,
                wrappingKey,
                "pin"
            );
        }
        await storage.storePinSalt(salt);
    }

    // Удаление метода входа. Последний метод удалить нельзя — иначе после
    // блокировки в аккаунт не попасть.
    async function removeAuthMethod(method: AuthMethod): Promise<void> {
        const methods = await storage.getAuthMethods();
        if (!methods.includes(method)) {
            return;
        }
        if (methods.length <= 1) {
            throw new Error("LAST_METHOD");
        }
        await storage.setAuthMethods(methods.filter((m) => m !== method));
        await storage.removeMethodData(method);
        if (method === "touchid") {
            // Секрет живёт в Keychain, не в IDB — чистим отдельно.
            await touchIdRemoveSecret();
        }
    }

    async function addPasswordFallback(password: string): Promise<void> {
        if (!encryptionKeyPair.value || !signKeyPair.value) {
            throw new Error("Keys not loaded");
        }
        const salt = randomBytes(16);
        const wrappingKey = await deriveFromPin(password, salt);
        await storage.storeEncryptedKeys(
            encryptionKeyPair.value,
            signKeyPair.value,
            wrappingKey,
            "pin"
        );
        // Тот же ключ устройства — под новый метод, чтобы обе разблокировки
        // давали один storageKey (иначе стор, зашифрованный им, не прочитается).
        if (storageKey.value) {
            await storage.storeEncryptedStorageKey(
                storageKey.value,
                wrappingKey,
                "pin"
            );
        }
        await storage.storePinSalt(salt);
        const methods = await storage.getAuthMethods();
        if (!methods.includes("pin")) {
            await storage.setAuthMethods([...methods, "pin"]);
        }
    }

    async function authenticate(): Promise<void> {
        const credentialId = await storage.getCredentialId();
        if (!credentialId) {
            throw new Error("No WebAuthn credential stored");
        }

        const assertion = (await navigator.credentials.get({
            publicKey: {
                challenge: randomBytes(32),
                allowCredentials: [{ type: "public-key", id: credentialId }],
                userVerification: "required",
                extensions: { prf: { eval: { first: PRF_SALT } } },
            },
        })) as PublicKeyCredential;

        const prfOutput =
            assertion.getClientExtensionResults().prf?.results?.first;
        if (!prfOutput) {
            throw new Error("PRF output not returned");
        }

        const wrappingKey = await importAesGcm(prfOutput);
        const { ecdh, sign } = await storage.loadKeys(wrappingKey, "prf");
        setKeys(ecdh, sign);
        const sk = await loadOrMigrateStorageKey(wrappingKey, "prf");
        await session.save(ecdh, sign, sk);
    }

    async function authenticateWithPassword(password: string): Promise<void> {
        checkLockout();
        const salt = await storage.getPinSalt();
        if (!salt) {
            throw new Error("No password salt stored");
        }
        const wrappingKey = await deriveFromPin(password, salt);
        try {
            const { ecdh, sign } = await storage.loadKeys(wrappingKey, "pin");
            resetLockout();
            setKeys(ecdh, sign);
            const sk = await loadOrMigrateStorageKey(wrappingKey, "pin");
            await session.save(ecdh, sign, sk);
        } catch (e) {
            if (e instanceof Error && e.message.startsWith("LOCKED:")) {
                throw e;
            }
            recordFailure();
            throw e;
        }
    }

    return {
        hasKeys: storage.hasKeys,
        getAuthMethods: storage.getAuthMethods,
        registerPrf,
        registerPassword,
        registerTouchId,
        linkWithPassword,
        linkWithPrf,
        linkWithTouchId,
        addPasswordFallback,
        addPrfFallback,
        addTouchIdFallback,
        changePin,
        removeAuthMethod,
        authenticate,
        authenticateWithPassword,
        authenticateWithTouchId,
        restoreFromSession: session.restore,
        clearSession: session.clear,
        failCount,
        lockoutUntil,
        maxAttempts: MAX_ATTEMPTS,
    };
}
